import type { HTMLAttributes } from 'react';
import styles from './ProgressBar.module.css';

export interface ProgressBarProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Current value of the progress bar (0-100)
   */
  value: number;
  /**
   * Maximum value (default: 100)
   */
  max?: number;
  /**
   * Color variant
   */
  color?: 'primary' | 'success' | 'warning' | 'error' | 'info';
  /**
   * Height variant
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Background color of the track
   */
  trackColor?: 'primary' | 'success' | 'warning' | 'error' | 'info';
  /**
   * Whether to animate the fill width change
   */
  showAnimation?: boolean;
}

export function ProgressBar({
  value,
  max = 100,
  color = 'primary',
  size = 'md',
  trackColor,
  showAnimation = true,
  className,
  'aria-label': ariaLabel,
  ...props
}: ProgressBarProps) {
  // Clamp value between 0 and max
  const clampedValue = Math.min(Math.max(value, 0), max);
  const percentage = Math.round((clampedValue / max) * 100);

  const containerClasses = [
    styles.container,
    styles[size],
    trackColor ? styles[`track-${trackColor}`] : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const fillClasses = [
    styles.fill,
    styles[color],
    showAnimation ? styles.animated : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      role="progressbar"
      aria-valuenow={clampedValue}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-label={ariaLabel}
      className={containerClasses}
      {...props}
    >
      <div
        className={fillClasses}
        style={{ width: `${percentage}%` }}
      />
    </div>
  );
}

ProgressBar.displayName = 'ProgressBar';
